/**
 * Poseidon dashboard — display formatting.
 *
 * Rules that matter for a trading UI:
 * - A missing value renders as an em dash, never as `0`. Zero and unknown are
 *   different facts, and conflating them is how a dashboard lies.
 * - Money, prices and durations are formatted from the values the daemon sends,
 *   not recomputed locally, so the screen and the ledger cannot disagree.
 */

export const EM_DASH = '—';

export function isMissing(value: number | null | undefined): boolean {
  return value === null || value === undefined || !Number.isFinite(value);
}

export function formatNumber(value: number | null | undefined, digits = 2): string {
  if (isMissing(value)) return EM_DASH;
  return (value as number).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatInteger(value: number | null | undefined): string {
  if (isMissing(value)) return EM_DASH;
  return Math.round(value as number).toLocaleString();
}

/** `fraction` is 0..1 (0.42 → "42.0%"). */
export function formatPercent(fraction: number | null | undefined, digits = 1): string {
  if (isMissing(fraction)) return EM_DASH;
  return `${((fraction as number) * 100).toFixed(digits)}%`;
}

/** For values already expressed in percent units (42 → "42.0%"). */
export function formatPercentValue(percent: number | null | undefined, digits = 1): string {
  if (isMissing(percent)) return EM_DASH;
  return `${(percent as number).toFixed(digits)}%`;
}

export function formatSigned(value: number | null | undefined, digits = 2): string {
  if (isMissing(value)) return EM_DASH;
  const numeric = value as number;
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toFixed(digits)}`;
}

export function formatCurrency(
  value: number | null | undefined,
  currency = 'USD',
  options: { digits?: number; signed?: boolean } = {},
): string {
  if (isMissing(value)) return EM_DASH;
  const digits = options.digits ?? 2;
  const numeric = value as number;
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Math.abs(numeric));
  if (numeric < 0) return `-${formatted}`;
  return options.signed === true && numeric > 0 ? `+${formatted}` : formatted;
}

export function formatPrice(value: number | null | undefined, digits = 5): string {
  if (isMissing(value)) return EM_DASH;
  return (value as number).toFixed(digits);
}

export function formatRatio(value: number | null | undefined, digits = 2): string {
  if (isMissing(value)) return EM_DASH;
  return `${(value as number).toFixed(digits)}×`;
}

/** Durations are milliseconds on the wire. */
export function formatDuration(ms: number | null | undefined): string {
  if (isMissing(ms)) return EM_DASH;
  const total = Math.max(0, Math.round(ms as number));
  if (total < 1_000) return `${total} ms`;
  const seconds = Math.floor(total / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${String(remSeconds).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${String(remMinutes).padStart(2, '0')}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${String(hours % 24).padStart(2, '0')}h`;
}

/** Compact age for staleness badges, e.g. "4.2 s", "3 m 12 s". */
export function formatAge(ms: number | null | undefined): string {
  if (isMissing(ms)) return EM_DASH;
  const total = Math.max(0, ms as number);
  if (total < 10_000) return `${(total / 1_000).toFixed(1)} s`;
  return formatDuration(total);
}

export function formatTimestamp(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso.length === 0) return EM_DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  return date.toLocaleTimeString(undefined, { hour12: false });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso.length === 0) return EM_DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  return date.toLocaleString(undefined, { hour12: false });
}

/** Renders an ISO timestamp together with the zone, so broker time is unambiguous. */
export function formatDateTimeWithZone(iso: string | null | undefined, timeZone?: string): string {
  if (iso === null || iso === undefined || iso.length === 0) return EM_DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  try {
    return date.toLocaleString(undefined, { hour12: false, timeZone, timeZoneName: 'short' });
  } catch {
    return date.toLocaleString(undefined, { hour12: false, timeZoneName: 'short' });
  }
}

export function formatBytes(bytes: number | null | undefined): string {
  if (isMissing(bytes)) return EM_DASH;
  const value = bytes as number;
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let index = 0;
  let scaled = value;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  const unit = units[index] ?? 'B';
  return `${scaled.toFixed(index === 0 ? 0 : 1)} ${unit}`;
}

export function formatCompact(value: number | null | undefined): string {
  if (isMissing(value)) return EM_DASH;
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value as number);
}

export function formatMs(value: number | null | undefined): string {
  if (isMissing(value)) return EM_DASH;
  return `${Math.round(value as number)} ms`;
}

/* ------------------------------------------------------------------ *
 * CSV export (§3.2 B "export to CSV")
 * ------------------------------------------------------------------ */

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

/**
 * Escape a field for CSV. Values are quoted when they contain a delimiter,
 * quote or newline, and embedded quotes are doubled (RFC 4180).
 */
export function csvEscape(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const header = columns.map((column) => csvEscape(column.header)).join(',');
  const lines = rows.map((row) => columns.map((column) => csvEscape(column.value(row))).join(','));
  return [header, ...lines].join('\r\n');
}

/**
 * Trigger a local download. The dashboard builds small exports client-side; the
 * daemon also exposes `/trades/closed/export.csv` for large windows, and this
 * helper is only used for what is already in the browser.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Inclusive percentage of a limit consumed; null when the limit is unknown. */
export function utilizationFraction(current: number | null, limit: number | null): number | null {
  if (isMissing(current) || isMissing(limit)) return null;
  const denominator = Math.abs(limit as number);
  if (denominator === 0) return null;
  return (current as number) / denominator;
}
